<script lang="ts" setup>
  import { onUnmounted, onMounted, type PropType } from 'vue';
  import { useTimer, purchaseEvent, closeConvertLeadEvent, useGoogleTagManager } from '@storefront/storefront';
  import { CartLayout, OrderPaymentSuccessBankTransfer } from '@storefront/components';
  import { useHttpClient, useAuthenticated, useViewQuery, useRoute } from '@othercommerce/foundation';
  import { BillingDriver } from '@othercommerce/domain';
  import type { OrderResource } from '@storefront/resources';

  defineOptions({
    layout: CartLayout,
    inheritAttrs: false,
  });

  const props = defineProps({
    order: { type: Object as PropType<OrderResource>, required: true },
    payment: { type: Object, required: true },
    method: { type: Object, required: true },
    isCeneoEnabled: { type: Boolean },
  });

  const http = useHttpClient();
  const authenticated = useAuthenticated();
  const query = useViewQuery();
  const gtm = useGoogleTagManager();
  const route = useRoute();

  const { formatted, stopTimer } = useTimer({
    timestamp: query.value?.expires,
    callback: () => {
      http.get(route('web.homepage.index'));
    },
  });

  const component = {
    [BillingDriver.PAYU]: null, // There is no more details to show for these drivers.
    [BillingDriver.PRZELEWY24]: null, // There is no more details to show for these drivers.
    [BillingDriver.FREE_PAYMENT]: null, // There is no more details to show for these drivers.
    [BillingDriver.BANK_TRANSFER]: OrderPaymentSuccessBankTransfer,
    [BillingDriver.CASH_ON_DELIVERY]: null, // There is no more details to show for these drivers.
    [BillingDriver.CUSTOMER_CREDIT]: null, // There is no more details to show for these drivers.
  };

  onMounted(() => {
    gtm.push(purchaseEvent(props.order));
    gtm.push(closeConvertLeadEvent(props.order));

    if (props.isCeneoEnabled && typeof window !== 'undefined' && (window as any)._ceneo) {
      (window as any)._ceneo('transaction', {
        client_email: props.order.acceptanceCeneo ? props.order.shippingEmail : '',
        order_id: String(props.order.id),
        shop_products: props.order.items.flatMap((item: any) => {
          if (item.product.type === 'SET' || item.product.type === 'FLEXIBLE') {
            return item.components.map((component: any) => ({
              id: String(component.variantUuid),
              price: Number(parseFloat(component.priceGross).toFixed(2)),
              quantity: parseInt(component.amount, 10),
              currency: props.order.currencyCode,
            }));
          }

          return [{
            id: String(
              item.product.type === 'VARIANT'
                ? item.variantUuid
                : item.product.uuid,
            ),
            price: Number(parseFloat(item.priceGross).toFixed(2)),
            quantity: parseInt(item.amount, 10),
            currency: props.order.currencyCode,
          }];
        }),
        amount: Number(parseFloat(props.order.itemsGross).toFixed(2)),
      });
    }
  });

  onUnmounted(() => {
    stopTimer();
  });
</script>
<template>
  <main id="main" class="order">
    <div class="order-success">
      <SfTile class="order-success__confirmation">
        <div class="order-success__icon-wrapper">
          <SfIconCheck aria-hidden="true" />
        </div>
        <div class="order-success__content">
          <article class="order-success__header">
            <header class="order-success__heading-wrapper">
              <h1 class="order-success__heading">{{ $t('SHOP.ORDER.HEADER.ORDER_SUCCESS') }}</h1>
            </header>
            <section class="order-success__message-wrapper">
              <p class="order-success__message">{{ $t('SHOP.ORDER.MESSAGE.ORDER_SUCCESS_EMAIL_NOTICE') }}</p>
              <p v-if="authenticated" class="order-success__account-info">{{ $t('SHOP.ORDER.MESSAGE.ORDER_SUCCESS_ACCOUNT_NOTICE') }}</p>
            </section>
          </article>

          <component :is="component[method.driver]" v-bind="{ order, payment, method }" />

          <article class="order-success__order">
            <section class="order-success__order-section">
              <p class="order-success__order-id">
                <span>{{ $t('SHOP.ORDER.LABEL.ORDER_NUMBER') }}</span>
                <strong>{{ order.number }}</strong>
              </p>
            </section>
          </article>

          <nav class="order-success__navigation">
            <SfButton :href="$route('web.homepage.index')" class="redirect" large primary>
              {{ $t('SHOP.ACTION.HOMEPAGE') }}
            </SfButton>
          </nav>

          <article class="order-success__security">
            <p class="order-success__security-text">
              <span>{{ $t('SHOP.LABEL.PAGE_EXPIRES') }}</span>
              <strong>{{ formatted }}</strong>
            </p>
          </article>
        </div>
      </SfTile>
    </div>
  </main>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .icon-check-marker {
    fill: var(--sf-order-success-icon-color-marker);
    stroke: var(--sf-order-success-icon-color-marker);
  }

  .icon-check-border {
    fill: var(--sf-order-success-icon-color);
    stroke: var(--sf-order-success-icon-color);
  }

  .order-success {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 0;

    &__confirmation {
      position: relative;
      width: 100%;
      max-width: 700px;
      margin-top: 3rem;
      text-wrap: pretty;
    }

    &__icon-wrapper {
      position: absolute;
      top: 0;
      left: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      transform: translate(-50%, -50%);
      height: 100px;
      width: 100px;
      font-size: 3rem;
      color: var(--sf-order-success-icon-color);
      border-radius: 50%;
    }

    &__content,
    &__header {
      display: flex;
      flex-direction: column;
    }

    &__content {
      gap: 1.5rem;
    }

    &__header {
      gap: 1rem;
    }

    &__heading {
      text-align: center;
      margin: 3rem 0 2rem;
    }

    &__order {
      font-size: 1.25rem;
    }

    &__order-id {
      word-break: break-word;
    }

    &__navigation {
      display: flex;
      justify-content: center;
      width: 100%;
      margin: 1rem 0;
    }

    &__security {
      font-size: 0.75rem;
    }
  }

  @include media.query(lg) {
    .order-success {
      margin: 0;
      padding: 1rem 0;

      &__icon-wrapper {
        width: 70px;
        height: 70px;

        svg {
          font-size: 2rem;
        }
      }

      &__confirmation {
        padding: 1rem 0;
      }

      &__header {
        text-align: center;
        margin-top: 1rem;
      }

      &__order {
        text-align: center;
      }

      &__heading {
        font-size: 1.5rem;
        margin: 1rem 0;
      }
    }

    @include media.query(md) {
      .order-success__security {
        text-align: center;
      }
    }
  }
</style>
